import { WorkflowQuickStudyFinding } from '@digital-twins/shared-types'

import { nameKeysFromExhibitCuts } from '@/components/quick-study-results/quickStudyPure'
import { ExhibitCutInput, ExhibitSpec, Result } from '@/components/quick-study-results/quickStudyTypes'
import { StudyDocQuote } from '@/components/study-doc'
import { studyDocQuoteFromExhibit } from '@/components/study-doc/enrichStudyDocQuote'
import {
  FAIRGEN_TIER_SENTENCES,
  normalizeFairgenConfidence,
} from '@/components/study-doc/StudyDocDecisionMasthead/formatStudyDocDecisionMasthead'

export interface ThreadStudyDocBlock {
  stepIndex: number
  findings: Array<WorkflowQuickStudyFinding>
}

/**
 * Structure-only exhibit spec. R4.4 consumes this read-only; S1 only
 * serializes a present payload. Matches crafted ExhibitSpec, camelCase.
 * `preAppliedCuts` are lowered cut ids (`attr::value` or a trimmed tag name).
 */
export interface ThreadStudyPlotSpec {
  plot?: string
  metric?: string
  preAppliedCuts?: Array<string>
  toggles?: Array<string>
}

/** FactBundle strip id. R5.3 fills the full-bleed shell from this map. */
export interface ThreadStudyStrip {
  id: string
  questionId?: string
}

export interface ThreadStudyDecisionVersionOdds {
  id: string
  pBestPct: number
  label?: string
}

/**
 * E1 output model (FE camelCase). R2.4 fills the masthead shell; S1 only
 * forwards a present payload. Absent → omitted (empty-stable).
 */
export interface ThreadStudyFairgenConfidence {
  tier: 'strong' | 'solid' | 'cautious' | 'do_not_call'
  sentence: string
}

export interface ThreadStudyDecisionConfidence {
  leaderId: string
  pBestPct: number
  basis: 'paired_bootstrap' | 'two_proportion'
  n: number
  claim: string
  perVersion?: Array<ThreadStudyDecisionVersionOdds>
  labels?: Record<string, string>
  recommendation?: string
  fairgenConfidence?: ThreadStudyFairgenConfidence
}

export interface ThreadStudyDocParts {
  markdown: string
  plots: Record<string, Result>
  quotes: Record<string, StudyDocQuote>
  plotSpecs?: Record<string, ThreadStudyPlotSpec>
  strips?: Record<string, ThreadStudyStrip>
  decisionConfidence?: ThreadStudyDecisionConfidence
  /**
   * Audience/population public token for mosaic slim-picker fills.
   * Absent → omit (initials fallback). Never invent; never a DB row id.
   */
  populationId?: string
}

/**
 * Audience public token stamped on the run (`inputs.audience` or the template's
 * audience-typed key). Empty / missing → undefined. Never invents.
 */
export function populationIdFromRunInputs(
  inputs: Record<string, string | null> | null | undefined,
  audienceKey = 'audience',
): string | undefined {
  if (!inputs) {
    return undefined
  }
  const raw = inputs[audienceKey]
  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return undefined
  }
  return raw.trim()
}

/** Public token on a QS finding payload when present (`audienceId` / `populationId`). */
function populationIdFromFinding(
  finding: WorkflowQuickStudyFinding,
): string | undefined {
  const payload = finding.result
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return undefined
  }
  const row = payload as Record<string, unknown>
  for (const key of ['populationId', 'audienceId'] as const) {
    const raw = row[key]
    if (typeof raw === 'string' && raw.trim().length > 0) {
      return raw.trim()
    }
  }
  return undefined
}

function normalizePopulationId(
  value: string | null | undefined,
): string | undefined {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return undefined
  }
  return value.trim()
}

export function plotIdForFinding(
  finding: WorkflowQuickStudyFinding,
  stepIndex: number,
  position: number,
): string {
  const payload = finding.result
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const carried = payload as Record<string, unknown>
    if (typeof carried.study_doc_plot_id === 'string' && carried.study_doc_plot_id.trim()) {
      return carried.study_doc_plot_id.trim()
    }
  }
  return `${stepIndex}-${position}`
}

function studyDocMarkdown(finding: WorkflowQuickStudyFinding): string {
  const payload = finding.result
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return ''
  }
  const raw = (payload as Record<string, unknown>).study_doc
  return typeof raw === 'string' ? raw.trim() : ''
}

function quoteLookupKeys(exhibitId: string): Array<string> {
  const id = exhibitId.trim()
  if (!id) {
    return []
  }
  const keys = [id]
  if (id.startsWith('quote:')) {
    keys.push(id.slice('quote:'.length))
  }
  return keys
}

function quotesFromFinding(
  finding: WorkflowQuickStudyFinding,
  result: Result | null,
): Record<string, StudyDocQuote> {
  const payload = finding.result
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return {}
  }
  const bundle = (payload as Record<string, unknown>).fact_bundle
  if (!bundle || typeof bundle !== 'object' || Array.isArray(bundle)) {
    return {}
  }
  const exhibits = (bundle as Record<string, unknown>).exhibits
  if (!exhibits || typeof exhibits !== 'object' || Array.isArray(exhibits)) {
    return {}
  }
  const rawQuotes = (exhibits as Record<string, unknown>).quotes
  if (!Array.isArray(rawQuotes)) {
    return {}
  }

  const quotes: Record<string, StudyDocQuote> = {}
  rawQuotes.forEach((entry) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return
    }
    const row = entry as Record<string, unknown>
    const id = typeof row.id === 'string' ? row.id : ''
    if (!id) {
      return
    }
    const quote = studyDocQuoteFromExhibit(row, result)
    if (!quote) {
      return
    }
    quoteLookupKeys(id).forEach((key) => {
      quotes[key] = quote
    })
  })
  return quotes
}

function findingPayload(
  finding: WorkflowQuickStudyFinding,
): Record<string, unknown> | null {
  const payload = finding.result
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null
  }
  return payload as Record<string, unknown>
}

function asStringArray(value: unknown): Array<string> | undefined {
  if (!Array.isArray(value)) {
    return undefined
  }
  const items = value.filter((entry): entry is string => typeof entry === 'string')
  return items.length > 0 ? items : undefined
}

function cutsFromUnknown(value: unknown): Array<string> | undefined {
  if (!Array.isArray(value)) {
    return undefined
  }
  const items = nameKeysFromExhibitCuts(value as Array<ExhibitCutInput>)
  return items.length > 0 ? items : undefined
}

function plotSpecFromUnknown(raw: unknown): ThreadStudyPlotSpec | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null
  }
  const row = raw as Record<string, unknown>
  const plot = typeof row.plot === 'string' && row.plot.trim() ? row.plot.trim() : undefined
  const metric =
    typeof row.metric === 'string' && row.metric.trim() ? row.metric.trim() : undefined
  const preAppliedCuts = cutsFromUnknown(row.preAppliedCuts ?? row.pre_applied_cuts)
  const toggles = asStringArray(row.toggles)
  if (!plot && !metric && !preAppliedCuts && !toggles) {
    return null
  }
  const spec: ThreadStudyPlotSpec = {}
  if (plot) {
    spec.plot = plot
  }
  if (metric) {
    spec.metric = metric
  }
  if (preAppliedCuts) {
    spec.preAppliedCuts = preAppliedCuts
  }
  if (toggles) {
    spec.toggles = toggles
  }
  return spec
}

function plotSpecsFromFinding(
  finding: WorkflowQuickStudyFinding,
  plotId: string,
): Record<string, ThreadStudyPlotSpec> {
  const payload = findingPayload(finding)
  if (!payload) {
    return {}
  }

  const specs: Record<string, ThreadStudyPlotSpec> = {}
  const carried = payload.plotSpecs ?? payload.plot_specs
  if (carried && typeof carried === 'object' && !Array.isArray(carried)) {
    Object.entries(carried as Record<string, unknown>).forEach(([key, value]) => {
      const spec = plotSpecFromUnknown(value)
      if (key && spec) {
        specs[key] = spec
      }
    })
  }

  const single = plotSpecFromUnknown(payload.exhibitSpec ?? payload.exhibit_spec)
  if (single && plotId) {
    specs[plotId] = single
  }

  return specs
}

function stripsFromFinding(finding: WorkflowQuickStudyFinding): Record<string, ThreadStudyStrip> {
  const payload = findingPayload(finding)
  if (!payload) {
    return {}
  }

  const strips: Record<string, ThreadStudyStrip> = {}
  const carried = payload.strips
  const bundle = payload.fact_bundle
  const exhibits =
    bundle && typeof bundle === 'object' && !Array.isArray(bundle)
      ? (bundle as Record<string, unknown>).exhibits
      : undefined
  const fromBundle =
    exhibits && typeof exhibits === 'object' && !Array.isArray(exhibits)
      ? (exhibits as Record<string, unknown>).strips
      : undefined
  const rawList = Array.isArray(carried) ? carried : Array.isArray(fromBundle) ? fromBundle : null

  if (rawList) {
    rawList.forEach((entry) => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
        return
      }
      const row = entry as Record<string, unknown>
      const id = typeof row.id === 'string' ? row.id.trim() : ''
      if (!id) {
        return
      }
      const questionRaw = row.questionId ?? row.question_id
      const questionId = typeof questionRaw === 'string' ? questionRaw.trim() : ''
      strips[id] = questionId ? { id, questionId } : { id }
    })
    return strips
  }

  if (carried && typeof carried === 'object' && !Array.isArray(carried)) {
    Object.entries(carried as Record<string, unknown>).forEach(([key, value]) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const row = value as Record<string, unknown>
        const id = typeof row.id === 'string' ? row.id.trim() : key.trim()
        if (id) {
          const questionRaw = row.questionId ?? row.question_id
          const questionId = typeof questionRaw === 'string' ? questionRaw.trim() : ''
          strips[id] = questionId ? { id, questionId } : { id }
        }
        return
      }
      if (typeof value === 'string' && value.trim()) {
        strips[value.trim()] = { id: value.trim() }
      }
    })
  }

  return strips
}

function decisionConfidenceFromFinding(
  finding: WorkflowQuickStudyFinding,
): ThreadStudyDecisionConfidence | undefined {
  const payload = findingPayload(finding)
  if (!payload) {
    return undefined
  }
  const raw = payload.decisionConfidence ?? payload.decision_confidence
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined
  }
  const row = raw as Record<string, unknown>
  const leaderId = typeof row.leaderId === 'string' ? row.leaderId : row.leader_id
  const pBestPct = typeof row.pBestPct === 'number' ? row.pBestPct : row.p_best_pct
  const n = row.n
  const basis = row.basis
  const claim = typeof row.claim === 'string' ? row.claim : ''
  if (typeof leaderId !== 'string' || !leaderId.trim()) {
    return undefined
  }
  if (typeof pBestPct !== 'number' || !Number.isFinite(pBestPct)) {
    return undefined
  }
  if (typeof n !== 'number' || !Number.isFinite(n)) {
    return undefined
  }
  if (basis !== 'paired_bootstrap' && basis !== 'two_proportion') {
    return undefined
  }
  if (!claim.trim()) {
    return undefined
  }

  const perRaw = row.perVersion ?? row.per_version
  const perVersion = Array.isArray(perRaw)
    ? perRaw.flatMap((entry) => {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
          return []
        }
        const item = entry as Record<string, unknown>
        const id = typeof item.id === 'string' ? item.id : ''
        const pct = typeof item.pBestPct === 'number' ? item.pBestPct : item.p_best_pct
        if (!id || typeof pct !== 'number' || !Number.isFinite(pct)) {
          return []
        }
        const labelRaw = item.label
        const label = typeof labelRaw === 'string' ? labelRaw.trim() : ''
        const odds: ThreadStudyDecisionVersionOdds = { id, pBestPct: pct }
        if (label) {
          odds.label = label
        }
        return [odds]
      })
    : undefined

  const next: ThreadStudyDecisionConfidence = {
    leaderId: leaderId.trim(),
    pBestPct,
    basis,
    n,
    claim: claim.trim(),
  }
  if (perVersion && perVersion.length > 0) {
    next.perVersion = perVersion
  }

  const labelsRaw = row.labels
  if (labelsRaw && typeof labelsRaw === 'object' && !Array.isArray(labelsRaw)) {
    const labels: Record<string, string> = {}
    Object.entries(labelsRaw as Record<string, unknown>).forEach(([key, value]) => {
      if (typeof key === 'string' && key.trim() && typeof value === 'string' && value.trim()) {
        labels[key.trim()] = value.trim()
      }
    })
    if (Object.keys(labels).length > 0) {
      next.labels = labels
    }
  }

  const fairgen = fairgenFromUnknown(
    row.fairgenConfidence ??
      row.fairgen_confidence ??
      payload.fairgenConfidence ??
      payload.fairgen_confidence,
  )
  if (fairgen) {
    next.fairgenConfidence = fairgen
  }

  const recommendation =
    readOptionalString(row.recommendation) ?? readOptionalString(payload.recommendation)
  if (recommendation) {
    next.recommendation = recommendation
  }

  return next
}

function readOptionalString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function fairgenFromUnknown(raw: unknown): ThreadStudyFairgenConfidence | undefined {
  const next = normalizeFairgenConfidence(raw)
  if (!next) {
    return undefined
  }
  const sentence = next.sentence?.trim() || FAIRGEN_TIER_SENTENCES[next.tier]
  return { tier: next.tier, sentence }
}

export function composeThreadStudyDoc(
  blocks: Array<ThreadStudyDocBlock>,
  toResult: (finding: WorkflowQuickStudyFinding) => Result | null,
  segmentBy?: string | null,
  /**
   * Optional audience/population public token already in hand (run inputs /
   * scope). Prefer this over a finding stamp when both exist.
   */
  populationId?: string | null,
): ThreadStudyDocParts {
  const markdownChunks: Array<string> = []
  const leftoverPlotTokens: Array<string> = []
  const plots: Record<string, Result> = {}
  const quotes: Record<string, StudyDocQuote> = {}
  const plotSpecs: Record<string, ThreadStudyPlotSpec> = {}
  const strips: Record<string, ThreadStudyStrip> = {}
  let decisionConfidence: ThreadStudyDecisionConfidence | undefined
  let resolvedPopulationId = normalizePopulationId(populationId)

  for (const block of blocks) {
    block.findings.forEach((finding, position) => {
      if (!resolvedPopulationId) {
        resolvedPopulationId = populationIdFromFinding(finding)
      }
      const result = toResult(finding)
      const doc = studyDocMarkdown(finding)
      if (doc) {
        markdownChunks.push(doc)
      }
      Object.assign(quotes, quotesFromFinding(finding, result))

      const plotId = plotIdForFinding(finding, block.stepIndex, position)
      Object.assign(plotSpecs, plotSpecsFromFinding(finding, plotId))
      Object.assign(strips, stripsFromFinding(finding))
      if (!decisionConfidence) {
        decisionConfidence = decisionConfidenceFromFinding(finding)
      }

      if (!result) {
        return
      }

      const token = `{{plot:${plotId}}}`
      if (!doc.includes(token)) {
        leftoverPlotTokens.push(token)
      }
      const spec: ExhibitSpec | undefined = plotSpecs[plotId]
      const stamped = spec ? ({ ...result, exhibitSpec: spec } as Result) : result
      plots[plotId] = segmentBy
        ? ({ ...stamped, segment_by: segmentBy } as Result)
        : stamped
    })
  }

  const markdown = [...markdownChunks, ...leftoverPlotTokens].filter(Boolean).join('\n\n')

  const parts: ThreadStudyDocParts = {
    markdown,
    plots,
    quotes,
  }
  if (Object.keys(plotSpecs).length > 0) {
    parts.plotSpecs = plotSpecs
  }
  if (Object.keys(strips).length > 0) {
    parts.strips = strips
  }
  if (decisionConfidence) {
    parts.decisionConfidence = decisionConfidence
  }
  if (resolvedPopulationId) {
    parts.populationId = resolvedPopulationId
  }
  return parts
}
